import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { List, Card, message, Spin, Modal, Button, Form, Input, Radio, Breadcrumb, Badge, Tooltip, Alert, Table, Tag, Tabs, Statistic, Row, Col, Space, Pagination, Popconfirm, Checkbox, Divider, Progress, Empty, Select } from 'antd';
import { DatabaseOutlined, CheckCircleOutlined, ClockCircleOutlined, CloudOutlined, DeleteFilled, SettingOutlined,  BarChartOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, FileTextOutlined, ApiOutlined, DatabaseFilled, DeploymentUnitOutlined, ThunderboltOutlined, AppstoreAddOutlined } from '@ant-design/icons';
import { accountService } from '../services/accountService';
import { API_BASE_URL } from '../services/config';
import fetchWrapper from '../components/utility/fetchWrapper.js';
import { readApiError } from '../utils/parseApiErrorMessage.js';
import { databricksService } from '../services/databricksService';
import { pipelineDiscoveryService } from '../services/pipelineDiscoveryService';
import { airflowPipelinesService } from '../services/airflowPipelinesService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import IsEnabled from "../rolepermission";
import EmrClusterStatus from "../components/EmrClusterStatus";
import AirflowDagStatus from "../components/AirflowDagStatus";
import PipelineRuntimeStatus from "../components/PipelineRuntimeStatus";
import { formatEmrDateTime, formatEmrElapsedTime } from "../utils/emrStatusHelpers";
import { getAirflowEnvironmentName, dedupePipelinesByStableKey, normalizeAirflowPipelineItem } from "../utils/pipelineDiscoveryHelpers";
import "./AccountsPage.css";

const AccountsPage = () => {
  const { user, accounts, setAccounts } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [databricksLoading, setDatabricksLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('aws');
  const [databricksAccounts, setDatabricksAccounts] = useState([]);
  const [hiddenClientIds, setHiddenClientIds] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddDatabricksModalVisible, setIsAddDatabricksModalVisible] = useState(false);
  const [hiddenArns, setHiddenArns] = useState([]);
  const [onboardMethod, setOnboardMethod] = useState(null);
  const [form] = Form.useForm();
  const [addDatabricksForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const isViewer = !IsEnabled('Accounts');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isPipelineDiscoveryModalVisible, setIsPipelineDiscoveryModalVisible] = useState(false);
  const [selectedAccountForDiscovery, setSelectedAccountForDiscovery] = useState(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState(null);
  const [autoOnboardEnabled, setAutoOnboardEnabled] = useState(false);
  const [onboardCloudwatch, setOnboardCloudwatch] = useState(true);
  const [onboardS3, setOnboardS3] = useState(true);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [airflowEnvironments, setAirflowEnvironments] = useState([]);
  const [selectedAirflowEnvironment, setSelectedAirflowEnvironment] = useState(null);
  const [airflowEnvironmentsLoading, setAirflowEnvironmentsLoading] = useState(false);
  const [airflowPipelinesLoading, setAirflowPipelinesLoading] = useState(false);

  const selectedEnvNameForAirflow = selectedAirflowEnvironment?.name || null;

  const columns = [
    {
      title: 'NAME',
      dataIndex: 'aws_account_name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: '500', color: isDark ? '#e6edf3' : '#333' }}>{text}</span>
          {/* <Badge
            status={
              record.status === 'completed'
                ? 'success'
                : record.status === 'pending'
                ? 'processing'
                : 'warning'
            }
          /> */}
        </div>
      ),
    },
    {
      title: 'Account ID',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 150,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', color: isDark ? '#adbac7' : '#666' }}>{text}</span>
      ),
    },

    {
      title: 'ARN',
      dataIndex: 'arn',
      key: 'arn',
      width: 200,
      render: (arn, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontFamily: 'monospace', color: isDark ? '#adbac7' : '#666' }}>
            {hiddenArns.includes(record.accountId)
              ? '*'.repeat(arn?.length || 0)
              : arn}
          </span>
          <Tooltip title={hiddenArns.includes(record.accountId) ? "Show ARN" : "Hide ARN"}>
        <Button
          type="text"
          style={{
            cursor: 'pointer',
            color: isDark ? '#8b949e' : '#888888',
            fontSize: '14px'
          }}
          onClick={(e) => toggleHiddenArn(record.accountId, e)}
          disabled={isViewer}
        >
          {hiddenArns.includes(record.accountId) ? 'Show' : 'Hide'}
        </Button>
      </Tooltip>
        </div>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        status === 'completed' ? (
          <Tag style={{
            background: '#16a34a',
            color: '#fff',
            border: '1px solid #16a34a',
            borderRadius: '8px',
            fontWeight: 500,
            padding: '4px 16px',
            
          }}>Active</Tag>
        ) : status === 'pending' ? (
          <Tag color="processing" style={{ borderRadius: '16px', padding: '4px 12px', fontWeight: '500', border: 'none' }}>Pending</Tag>
        ) : (
          <Tag color="warning" style={{ borderRadius: '16px', padding: '4px 12px', fontWeight: '500', border: 'none' }}>Inactive</Tag>
        )
      ),
    },
    {
      title: 'ACTION',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteFilled />}
              type="text"
              danger
              size="small"
              onClick={(e) => handleDeleteAccount(e, record.accountId)}
              disabled={isViewer}
            />
          </Tooltip>
          <Tooltip title="Explore Logs">
            <Button
              icon={<SettingOutlined />}
              type="text"
              size="small"
              onClick={() => handleExploreLogs(record)}
              disabled={isViewer}
            />
          </Tooltip>
          <Tooltip title="Explore Metrics">
            <Button
              icon={<BarChartOutlined />}
              type="text"
              size="small"
              onClick={() => handleExploreMetrics(record)}
              disabled={isViewer}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await accountService.getAccounts();
      const mappedDataArray = [];
      
      // Transform the new data structure: { clientId: { accountId: accountData } }
      for (const clientId in data) {
        for (const accountId in data[clientId]) {
          mappedDataArray.push({ 
            ...data[clientId][accountId], 
            accountId: accountId,
            clientId: clientId
          });
        }
      }
      
      setAccounts(mappedDataArray);
      // Initialize all ARNs as hidden by default
      const allAccountIds = mappedDataArray.map(acc => acc.accountId);
      setHiddenArns(allAccountIds);
    } catch (error) {
      message.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  }, [setAccounts]);

  const fetchDatabricksAccounts = useCallback(async () => {
    setDatabricksLoading(true);
    try {
      const accounts = await databricksService.getAccounts();
      
      if (!accounts || !Array.isArray(accounts)) {
        message.warning('Received invalid account data format');
        setDatabricksAccounts([]);
        return;
      }
      
      // Map the API response directly to the expected format
      const accountsWithDetails = accounts.map((account) => {
        if (!account || !account.account_id) {
          return null;
        }
        return {
          accountId: account.account_id,
          accountUrl: account.account_url,
          clientId: account.client_id,
          updatedAt: account.updated_at,
          accountName: account.account_name || `Databricks Account ${account.account_id}`,
          status: 'Connected',
        };
      }).filter(Boolean); // Remove any null entries
      
      setDatabricksAccounts(accountsWithDetails);
      // Initialize all Client IDs as hidden by default
      const allDatabricksAccountIds = accountsWithDetails.map(acc => acc.accountId);
      setHiddenClientIds(allDatabricksAccountIds);
    } catch (error) {
      console.error('Error fetching Databricks accounts:', error);
      const errorMessage = error.message || 'Failed to fetch Databricks accounts';
      message.error(`Failed to fetch Databricks accounts: ${errorMessage}`);
      setDatabricksAccounts([]);
    } finally {
      setDatabricksLoading(false);
    }
  }, []);

  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => 
      account.aws_account_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.accountId?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [accounts, searchQuery]);

  const paginatedAccounts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAccounts.slice(start, start + pageSize);
  }, [filteredAccounts, currentPage, pageSize]);

  // Same for Databricks
  const filteredDatabricksAccounts = useMemo(() => {
    return databricksAccounts.filter(account => 
      account.accountName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.accountId?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [databricksAccounts, searchQuery]);

  const paginatedDatabricksAccounts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredDatabricksAccounts.slice(start, start + pageSize);
  }, [filteredDatabricksAccounts, currentPage, pageSize]);
  useEffect(() => {
    fetchAccounts();
    fetchDatabricksAccounts();
  }, [fetchAccounts, fetchDatabricksAccounts]);

    const awsStats = useMemo(() => ({
    total: accounts.length,
    active: accounts.filter(a => a.status === 'completed').length,
    pending: accounts.filter(a => a.status === 'pending').length,
  }), [accounts]);

  const databricksStats = useMemo(() => ({
    total: databricksAccounts.length,
    connected: databricksAccounts.filter(a => a.status === 'Connected').length,
  }), [databricksAccounts]);

  // Handle URL parameter for tab selection
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'databricks' || tabParam === 'aws') {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  useEffect(() => {
  setCurrentPage(1);
}, [searchQuery, activeTab]);

  // Changed function name and navigation path
  const handleExploreMetrics = (account) => {
    setSelectedAccount(account);
    navigate(`/metrics?accountId=${account.accountId}`);
  };

  const handleExploreLogs = (account) => {
    setSelectedAccount(account);
    navigate(`/logs?accountId=aws_${account.accountId}`);
  };

  const handleDiscoverPipelines = (account) => {
    setSelectedAccountForDiscovery(account);
    setIsPipelineDiscoveryModalVisible(true);
    setDiscoveryResults(null);
    setAutoOnboardEnabled(false);
    setOnboardCloudwatch(true);
    setOnboardS3(true);
    setOnboardingProgress(null);
    setAirflowEnvironments([]);
    setSelectedAirflowEnvironment(null);
  };

  const loadAirflowEnvironments = useCallback(async (account) => {
    if (!account?.accountId) return;
    const clientId = account.clientId || user?.client_id;
    setAirflowEnvironmentsLoading(true);
    try {
      const res = await airflowPipelinesService.getEnvironments(
        account.accountId,
        clientId,
      );
      const environments = res.environments || [];
      setAirflowEnvironments(environments);
      setSelectedAirflowEnvironment((prev) => prev || environments[0] || null);
    } catch (err) {
      console.error("Error fetching Airflow environments:", err);
      setAirflowEnvironments([]);
    } finally {
      setAirflowEnvironmentsLoading(false);
    }
  }, [user?.client_id]);

  useEffect(() => {
    if (!isPipelineDiscoveryModalVisible || !selectedAccountForDiscovery) return;
    loadAirflowEnvironments(selectedAccountForDiscovery);
  }, [
    isPipelineDiscoveryModalVisible,
    selectedAccountForDiscovery,
    loadAirflowEnvironments,
  ]);

  const mergeAirflowDagsIntoDiscovery = useCallback((normalizedItems, envName) => {
    setDiscoveryResults((prev) => {
      if (!prev?.pipelines) return prev;
      const otherDags = (prev.pipelines.airflow_dags || []).filter((dag) => {
        const dagEnv = getAirflowEnvironmentName(dag);
        return dagEnv && dagEnv !== envName;
      });
      const airflow_dags = dedupePipelinesByStableKey([
        ...otherDags,
        ...normalizedItems,
      ]);
      return {
        ...prev,
        pipelines: {
          ...prev.pipelines,
          airflow_dags,
        },
        summary: {
          ...prev.summary,
          total_airflow_dags: airflow_dags.length,
          total_pipelines:
            (prev.pipelines.glue_jobs?.length || 0) +
            (prev.pipelines.step_functions?.length || 0) +
            (prev.pipelines.emr_clusters?.length || 0) +
            airflow_dags.length,
        },
      };
    });
  }, []);

  const handleDiscoverAirflowDags = async () => {
    const envName = selectedAirflowEnvironment?.name;
    const envRegion = selectedAirflowEnvironment?.region || null;
    const awsAccountId = selectedAccountForDiscovery?.accountId;
    const clientId = selectedAccountForDiscovery?.clientId || user?.client_id;
    if (!envName) {
      message.warning("Please select an Airflow (MWAA) environment");
      return;
    }
    if (!clientId || !awsAccountId) {
      message.warning("Account and client ID are required to discover DAGs");
      return;
    }
    setAirflowPipelinesLoading(true);
    try {
      const res = await airflowPipelinesService.discoverDags(
        envName,
        awsAccountId,
        envRegion,
        clientId,
      );
      const discoverySource = res.debug?.source || res.source || null;
      const normalizedItems = (res.items || []).map((item) =>
        normalizeAirflowPipelineItem(item, envName, envRegion, discoverySource),
      );
      if (discoveryResults?.pipelines) {
        mergeAirflowDagsIntoDiscovery(normalizedItems, envName);
      } else {
        setDiscoveryResults({
          pipelines: {
            glue_jobs: [],
            step_functions: [],
            emr_clusters: [],
            airflow_dags: dedupePipelinesByStableKey(normalizedItems),
          },
          summary: {
            total_pipelines: normalizedItems.length,
            total_glue_jobs: 0,
            total_step_functions: 0,
            total_emr_clusters: 0,
            total_airflow_dags: normalizedItems.length,
            total_cloudwatch_logs: 0,
            total_s3_logs: 0,
            total_log_paths: 0,
          },
          log_paths: { cloudwatch: [], s3: [] },
        });
      }
      message.success(`Discovered ${res.count ?? normalizedItems.length} Airflow DAG(s)`);
    } catch (err) {
      console.error("Airflow discover error:", err);
      message.error(err.message || "Failed to discover Airflow DAGs");
    } finally {
      setAirflowPipelinesLoading(false);
    }
  };

  const handleStartDiscovery = async () => {
    if (!selectedAccountForDiscovery) return;

    setDiscoveryLoading(true);
    setDiscoveryResults(null);
    setOnboardingProgress(null);

    try {
      const clientId = selectedAccountForDiscovery.clientId || user?.client_id;
      if (!clientId) {
        message.error("Missing client id. Please sign out and sign in again or contact an administrator.");
        setDiscoveryLoading(false);
        return;
      }
      const result = await pipelineDiscoveryService.discoverPipelines(
        clientId,
        selectedAccountForDiscovery.accountId,
        {
          regions: null, // Will use default
          autoOnboard: autoOnboardEnabled,
          onboardCloudwatch: onboardCloudwatch,
          onboardS3: onboardS3
        }
      );

      setDiscoveryResults(result);

      if (autoOnboardEnabled && result.onboarding_results) {
        setOnboardingProgress(result.onboarding_results);
        const cwNew = result.onboarding_results.cloudwatch?.onboarded?.length || 0;
        const s3New = result.onboarding_results.s3?.onboarded?.length || 0;
        const cwExisting = result.onboarding_results.cloudwatch?.already_onboarded?.length || 0;
        const s3Existing = result.onboarding_results.s3?.already_onboarded?.length || 0;
        if (cwNew + s3New > 0) {
          message.success(`Discovered ${result.summary.total_pipelines} pipelines and onboarded ${cwNew + s3New} log paths`);
        } else if (cwExisting + s3Existing > 0) {
          message.info(`Discovered ${result.summary.total_pipelines} pipelines — all ${cwExisting + s3Existing} log paths were already onboarded`);
        } else {
          message.success('Pipelines discovered and logs onboarded successfully!');
        }
      } else {
        message.success(`Discovered ${result.summary.total_pipelines} pipelines with ${result.summary.total_log_paths} log paths`);
      }
    } catch (error) {
      console.error('Pipeline discovery error:', error);
      message.error(error.message || 'Failed to discover pipelines');
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleOnboardDiscoveredLogs = async () => {
    if (!discoveryResults || !selectedAccountForDiscovery) return;

    setDiscoveryLoading(true);
    setOnboardingProgress(null);

    try {
      const clientId = selectedAccountForDiscovery.clientId || user?.client_id;
      if (!clientId) {
        message.error("Missing client id. Please sign out and sign in again or contact an administrator.");
        setDiscoveryLoading(false);
        return;
      }
      const result = await pipelineDiscoveryService.autoOnboardLogs(
        clientId,
        selectedAccountForDiscovery.accountId,
        discoveryResults.log_paths,
        {
          onboardCloudwatch: onboardCloudwatch,
          onboardS3: onboardS3
        }
      );

      setOnboardingProgress(result.results);
      const totalNew = result.summary?.total_onboarded || 0;
      const totalExisting = result.summary?.total_already_onboarded || 0;
      if (totalNew > 0) {
        message.success(`Onboarded ${totalNew} log paths successfully!`);
      } else if (totalExisting > 0) {
        message.info(`All ${totalExisting} log paths were already onboarded`);
      } else {
        message.success('Onboarding complete');
      }
    } catch (error) {
      console.error('Auto-onboarding error:', error);
      message.error(error.message || 'Failed to onboard logs');
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const showOnboardModal = () => {
    setIsModalVisible(true);
  };

  const validateAwsAccountId = (_, value) => {
    const id = String(value || '').trim();
    if (!id) return Promise.reject(new Error('Please enter AWS Account ID'));
    if (!/^\d{12}$/.test(id)) {
      return Promise.reject(new Error('AWS Account ID must be exactly 12 digits'));
    }
    return Promise.resolve();
  };

  const validateIamRoleArn = (_, value) => {
    const arn = String(value || '').trim();
    if (!arn) return Promise.reject(new Error('Please enter the IAM Role ARN'));
    if (!/^arn:aws:iam::\d{12}:role\/[\w+=,.@\-_/]+$/.test(arn)) {
      return Promise.reject(
        new Error(
          'Please enter a valid Role ARN (arn:aws:iam::<account-id>:role/<role-name>)'
        )
      );
    }
    return Promise.resolve();
  };

  const handleOnboard = async (values) => {
    try {
      if (onboardMethod === 'accessKey') {
        const accountData = {
          client_id: values.client_id || user?.client_id,
          aws_account_name: values.awsAccountName,
          aws_account_id: values.awsAccountId,
          access_key_group: {
            access_key: values.awsAccessKey || '',
            secret_key: values.awsSecretKey || '',
            session_token: values.awsSessionToken || '',
          },
          session_group: {
            session_token: 'predefined-session-token',
          },
          external_id: false,
          additional_policy: [],
        };
        await accountService.createAccount(accountData);
        message.success('Account onboarded successfully');
      } else if (onboardMethod === 'iamRole') {
        const payload = {
          client_id: user?.client_id,
          aws_account_name: values.awsAccountName,
          customer_account_id: values.awsAccountId,
          customer_role_arn: values.customerRoleArn,
          external_id: values.externalId || undefined,
        };
        const response = await fetchWrapper(`${API_BASE_URL}/api/v1/aws/onboard/iam-role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          throw await readApiError(response, 'Failed to onboard account via IAM role');
        }
        await response.json();
        message.success('Account onboarded successfully using IAM Role');
      } else {
        message.info('This onboarding method is not yet implemented.');
        return;
      }
      form.resetFields();
      setIsModalVisible(false);
      await fetchAccounts();
    } catch (error) {
      console.error('Onboarding failed:', error);
      message.error(error.message || 'Failed to onboard account');
    }
  };

  const toggleHiddenArn = (accountId, e) => {
    e.stopPropagation();
    if (hiddenArns.includes(accountId)) {
      setHiddenArns(hiddenArns.filter((id) => id !== accountId));
    } else {
      setHiddenArns([...hiddenArns, accountId]);
    }
  };
  
  // New function to show delete message
  const handleDeleteAccount = (e, accountId) => {
    e.stopPropagation();
    message.info('Delete functionality coming soon');
  };

  // Helper function to store credentials in localStorage
  const storeCredentials = (accountId, credentials) => {
    try {
      localStorage.setItem(`databricks_credentials_${accountId}`, JSON.stringify({
        client_id: credentials.client_id,
        client_secret: credentials.client_secret,
        pat_token: credentials.pat_token,
        account_url: credentials.account_url,
      }));
    } catch (error) {
      console.error('Error storing in localStorage:', error);
    }
  };

  // Validate UUID format
  const validateUUID = (_, value) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!value) {
      return Promise.reject(new Error('Please enter a valid UUID'));
    }
    if (!uuidRegex.test(value)) {
      return Promise.reject(new Error('Please enter a valid UUID'));
    }
    return Promise.resolve();
  };

  // Handle adding Databricks account
  const handleAddDatabricksAccount = async (values) => {
    try {
      // Check if account is already onboarded
      const existingAccount = databricksAccounts.find(
        (account) => account.accountId === values.accountId
      );
      
      if (existingAccount) {
        message.warning('This account is already onboarded');
        return;
      }

      // Prepare payload with only required fields for backend API
      const payload = {
        account_id: values.accountId,
        account_url: values.accountUrl || 'https://accounts.cloud.databricks.com',
        client_id: values.clientId,
        client_secret: values.clientSecret,
      };

      await databricksService.onboardAccount(payload);
      
      // Store onboarding credentials for subsequent Databricks operations
      storeCredentials(values.accountId, {
        client_id: values.clientId,
        client_secret: values.clientSecret,
        account_url: values.accountUrl || 'https://accounts.cloud.databricks.com',
      });

      message.success('Databricks account onboarded successfully');
      setIsAddDatabricksModalVisible(false);
      addDatabricksForm.resetFields();
      await fetchDatabricksAccounts();
    } catch (error) {
      const errorMessage = error.message || 'Failed to onboard Databricks account';
      message.error(errorMessage);
      console.error('Onboarding error:', error);
    }
  };

  const toggleHiddenClientId = (accountId, e) => {
    e.stopPropagation();
    if (hiddenClientIds.includes(accountId)) {
      setHiddenClientIds(hiddenClientIds.filter((id) => id !== accountId));
    } else {
      setHiddenClientIds([...hiddenClientIds, accountId]);
    }
  };

  const handleDeleteDatabricksAccount = async (record) => {
    try {
      await databricksService.deleteAccount(record.accountId);
      message.success('Databricks account deleted successfully');
      await fetchDatabricksAccounts();
      localStorage.removeItem(`databricks_credentials_${record.accountId}`);
    } catch (error) {
      message.error(error.message || 'Failed to delete Databricks account');
    }
  };

  // Databricks accounts columns
  const databricksColumns = [
    {
      title: 'ACCOUNT NAME & ID',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 300,
      render: (text, record) => {
        // Always show account name if available, otherwise show account ID
        const displayName = record.accountName || text;
        const showAccountId = record.accountName && record.accountName !== `Databricks Account ${text}`;
        
        return (
          <div>
            <div style={{ fontWeight: '500', color: isDark ? '#e6edf3' : '#333', marginBottom: showAccountId ? '4px' : '0' }}>
              {displayName}
            </div>
            {showAccountId && (
              <div style={{ fontSize: '12px', color: isDark ? '#8b949e' : '#999', fontFamily: 'monospace' }}>
                {text}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Client ID',
      dataIndex: 'clientId',
      key: 'clientId',
      width: 200,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontFamily: 'monospace', color: isDark ? '#adbac7' : '#666' }}>
            {hiddenClientIds.includes(record.accountId)
              ? text
              : '*'.repeat(text?.length || 0)}
          </span>
          <Tooltip title={hiddenClientIds.includes(record.accountId) ? "Hide Client ID" : "Show Client ID"}>
            <Button
              type="text"
              style={{
                cursor: 'pointer',
                color: isDark ? '#8b949e' : '#888888',
                fontSize: '14px'
              }}
              onClick={(e) => toggleHiddenClientId(record.accountId, e)}
            >
              {hiddenClientIds.includes(record.accountId) ? 'Show' : 'Hide'}
            </Button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag
          style={{
            background: status === 'Connected' ? '#16a34a' : '#ff4d4f',
            color: '#fff',
            border: '1px solid',
            borderColor: status === 'Connected' ? '#16a34a' : '#ff4d4f',
            borderRadius: '8px',
            fontWeight: 500,
            padding: '4px 16px',
          }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            title="Delete account"
            description="Are you sure you want to delete this Databricks account?"
            onConfirm={() => handleDeleteDatabricksAccount(record)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <Button
                icon={<DeleteFilled />}
                type="text"
                danger
                size="small"
                onClick={(e) => e.stopPropagation()}
                disabled={isViewer}
              />
            </Tooltip>
          </Popconfirm>
          <Tooltip title="Configure Logs">
            <Button
              icon={<SettingOutlined />}
              type="text"
              size="small"
              onClick={() => navigate(`/logs?accountId=databricks_${record.accountId}`)}
            />
          </Tooltip>
          <Tooltip title="Configure Metrics">
            <Button
              icon={<BarChartOutlined />}
              type="text"
              size="small"
              onClick={() => navigate(`/databricks-metrics-onboarding?accountId=databricks_${record.accountId}`)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const awsRowStatus = (status) =>
    status === "completed" ? "completed" : status === "pending" ? "pending" : "inactive";

  return (
    <div className={`accounts-page ${isDark ? "accounts-page--dark" : "accounts-page--light"}`}>
      <div className="accounts-page__toolbar">
        <Breadcrumb className="accounts-page__breadcrumb">
          <Breadcrumb.Item>Home</Breadcrumb.Item>
          <Breadcrumb.Item>Accounts</Breadcrumb.Item>
        </Breadcrumb>
        <Space className="accounts-page__actions">
          <Tooltip title={activeTab === 'aws' ? "Refresh AWS Accounts" : "Refresh Databricks Accounts"}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => activeTab === 'aws' ? fetchAccounts() : fetchDatabricksAccounts()}
              loading={activeTab === 'aws' ? loading : databricksLoading}
              size="large"
            >
              Refresh
            </Button>
          </Tooltip>
          {activeTab === 'aws' && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              size="large"
              onClick={showOnboardModal} 
              disabled={isViewer}
            >
              Onboard Account
            </Button>
          )}
          {activeTab === 'databricks' && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setIsAddDatabricksModalVisible(true)} 
              disabled={isViewer}
            >
              Add Databricks Account
            </Button>
          )}
        </Space>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="accounts-page__tabs"
        items={[
          {
            key: 'aws',
            label: (
              <span className="accounts-page__tab-label">
                AWS Accounts
                <Badge count={accounts.length} className="accounts-page__tab-badge" showZero />
              </span>
            ),
            // children: (
            //   <Spin spinning={loading}>
            //     <div style={{ 
            //       border: '1px solid #f0f0f0', 
            //       borderRadius: '12px', 
            //       overflow: 'hidden',
            //       background: '#fff'
            //     }}>
            //       <Table      
            //         className="custom-ant-table rounded-rows-table"
            //         columns={columns}
            //         dataSource={accounts}
            //         loading={loading}
            //         rowKey="accountId"
            //         pagination={false}
            //       />
            //     </div>
            //   </Spin>
            // ),
            children: (
              <div>
                <Row gutter={[12, 12]} className="accounts-page__stats-row">
                  <Col xs={24} sm={12} md={8}>
                    <Card bordered={false} className="accounts-page__stat-card accounts-page__stat-card--violet">
                      <Statistic
                        title={<span style={{ color: "rgba(255,255,255,0.92)" }}>Total Accounts</span>}
                        value={awsStats.total}
                        valueStyle={{ color: "#fff", fontSize: 30, fontWeight: 700 }}
                        prefix={<DatabaseOutlined style={{ color: "#fff", fontSize: 22 }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card bordered={false} className="accounts-page__stat-card accounts-page__stat-card--green">
                      <Statistic
                        title={<span style={{ color: "rgba(255,255,255,0.92)" }}>Active Accounts</span>}
                        value={awsStats.active}
                        valueStyle={{ color: "#fff", fontSize: 30, fontWeight: 700 }}
                        prefix={<CheckCircleOutlined style={{ color: "#fff", fontSize: 22 }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card bordered={false} className="accounts-page__stat-card accounts-page__stat-card--amber">
                      <Statistic
                        title={<span style={{ color: "rgba(255,255,255,0.92)" }}>Pending Accounts</span>}
                        value={awsStats.pending}
                        valueStyle={{ color: "#fff", fontSize: 30, fontWeight: 700 }}
                        prefix={<ClockCircleOutlined style={{ color: "#fff", fontSize: 22 }} />}
                      />
                    </Card>
                  </Col>
                </Row>

                <Input
                  className="accounts-page__search"
                  placeholder="Search accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  prefix={<SearchOutlined />}
                />

                <div className="accounts-page__data-header accounts-page__data-header--aws">
                  <div className="accounts-page__h-col accounts-page__h-col--name">Name &amp; ID</div>
                  <div className="accounts-page__h-col accounts-page__h-col--arn">ARN</div>
                  <div className="accounts-page__h-col accounts-page__h-col--status">Status</div>
                  <div className="accounts-page__h-col accounts-page__h-col--actions">Actions</div>
                </div>
                {/* Account Cards */}
                <Spin spinning={loading}>
                  {paginatedAccounts.map((account) => (
                    <Card
                      key={account.accountId}
                      className="accounts-page__list-card"
                      data-row-status={awsRowStatus(account.status)}
                    >
                      <div className="accounts-page__row accounts-page__row--aws">
                        <div className="accounts-page__primary-cell">
                          <div className="accounts-page__account-title">{account.aws_account_name}</div>
                          <Space size={8} className="accounts-page__meta-tags">
                            <Tag className="accounts-page__id-tag">ID: {account.accountId}</Tag>
                          </Space>
                        </div>

                        <div className="accounts-page__arn-cell">
                          <span className="accounts-page__arn-text" title={account.arn}>
                            {hiddenArns.includes(account.accountId) ? "*".repeat(account.arn?.length || 0) : account.arn}
                          </span>
                          <Tooltip title={hiddenArns.includes(account.accountId) ? "Show ARN" : "Hide ARN"}>
                            <Button type="text" className="accounts-page__link-btn" onClick={(e) => toggleHiddenArn(account.accountId, e)}>
                              {hiddenArns.includes(account.accountId) ? "Show" : "Hide"}
                            </Button>
                          </Tooltip>
                        </div>

                        <div className="accounts-page__status-cell">
                          <Tag
                            className={`accounts-page__status-tag ${
                              account.status === "completed"
                                ? "accounts-page__status-tag--ok"
                                : account.status === "pending"
                                ? "accounts-page__status-tag--pending"
                                : "accounts-page__status-tag--bad"
                            }`}
                          >
                            {account.status === "completed" ? "Active" : account.status === "pending" ? "Pending" : "Inactive"}
                          </Tag>
                        </div>

                        <Space size={8} className="accounts-page__actions" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Delete">
                            <Button
                              icon={<DeleteFilled />}
                              type="text"
                              danger
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                message.info('Delete functionality coming soon');
                              }}
                              disabled={isViewer}
                            />
                          </Tooltip>
                          <Tooltip title="Explore Logs">
                            <Button
                              icon={<SettingOutlined />}
                              type="text"
                              size="small"
                              onClick={() => handleExploreLogs(account)}
                              disabled={isViewer}
                            />
                          </Tooltip>
                          <Tooltip title="Explore Metrics">
                            <Button
                              icon={<BarChartOutlined />}
                              type="text"
                              size="small"
                              onClick={() => handleExploreMetrics(account)}
                              disabled={isViewer}
                            />
                          </Tooltip>
                          <Tooltip title="Discover Pipelines">
                            <Button
                              icon={<DeploymentUnitOutlined />}
                              type="text"
                              size="small"
                              onClick={() => handleDiscoverPipelines(account)}
                              disabled={isViewer}
                            />
                          </Tooltip>
                          <Tooltip title="Discover Applications">
                            <Button
                              icon={<AppstoreAddOutlined />}
                              type="text"
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/applications?accountId=${encodeURIComponent(account.accountId)}`
                                )
                              }
                              disabled={isViewer}
                            />
                          </Tooltip>
                        </Space>
                      </div>
                    </Card>
                  ))}
                </Spin>

                {filteredAccounts.length > 0 && (
                  <div className="accounts-page__pagination-wrap">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={filteredAccounts.length}
                      onChange={(page) => setCurrentPage(page)}
                      showSizeChanger
                      showTotal={(total) => <span>Total {total} accounts</span>}
                    />
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'databricks',
            label: (
              <span className="accounts-page__tab-label">
                Databricks Accounts
                <Badge count={databricksAccounts.length} className="accounts-page__tab-badge" showZero />
              </span>
            ),
            // children: (
            //   <Spin spinning={databricksLoading}>
            //     <div style={{ 
            //       border: '1px solid #f0f0f0', 
            //       borderRadius: '12px', 
            //       overflow: 'hidden',
            //       background: '#fff'
            //     }}>
            //       <Table      
            //         className="custom-ant-table rounded-rows-table"
            //         columns={databricksColumns}
            //         dataSource={databricksAccounts}
            //         loading={databricksLoading}
            //         rowKey="accountId"
            //         pagination={false}
            //       />
            //     </div>
            //   </Spin>
            // ),
            children: (
              <div>
                <Row gutter={[12, 12]} className="accounts-page__stats-row">
                  <Col xs={24} sm={12} md={12}>
                    <Card bordered={false} className="accounts-page__stat-card accounts-page__stat-card--violet">
                      <Statistic
                        title={<span style={{ color: "rgba(255,255,255,0.92)" }}>Total Accounts</span>}
                        value={databricksStats.total}
                        valueStyle={{ color: "#fff", fontSize: 30, fontWeight: 700 }}
                        prefix={<CloudOutlined style={{ color: "#fff", fontSize: 22 }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={12}>
                    <Card bordered={false} className="accounts-page__stat-card accounts-page__stat-card--green">
                      <Statistic
                        title={<span style={{ color: "rgba(255,255,255,0.92)" }}>Connected Accounts</span>}
                        value={databricksStats.connected}
                        valueStyle={{ color: "#fff", fontSize: 30, fontWeight: 700 }}
                        prefix={<CheckCircleOutlined style={{ color: "#fff", fontSize: 22 }} />}
                      />
                    </Card>
                  </Col>
                </Row>

                <Input
                  className="accounts-page__search"
                  placeholder="Search Databricks accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  prefix={<SearchOutlined />}
                />

                <div className="accounts-page__data-header accounts-page__data-header--databricks">
                  <div className="accounts-page__h-col accounts-page__h-col--name">Account name &amp; ID</div>
                  <div className="accounts-page__h-col accounts-page__h-col--client">Client ID</div>
                  <div className="accounts-page__h-col accounts-page__h-col--status">Status</div>
                  <div className="accounts-page__h-col accounts-page__h-col--actions">Actions</div>
                </div>

                {/* Account Cards */}
                <Spin spinning={databricksLoading}>
                  {paginatedDatabricksAccounts.map((account) => (
                    <Card
                      key={account.accountId}
                      className="accounts-page__list-card"
                      data-row-status={account.status === "Connected" ? "connected" : "disconnected"}
                    >
                      <div className="accounts-page__row accounts-page__row--databricks">
                        <div className="accounts-page__primary-cell">
                          <div className="accounts-page__account-title">{account.accountName}</div>
                          <div className="accounts-page__cell-muted">{account.accountId}</div>
                        </div>

                        <div className="accounts-page__client-block">
                          <span className="accounts-page__cell-muted" title={account.clientId}>
                            {hiddenClientIds.includes(account.accountId)
                              ? account.clientId
                              : "*".repeat(account.clientId?.length || 0)}
                          </span>
                          <Tooltip title={hiddenClientIds.includes(account.accountId) ? "Hide Client ID" : "Show Client ID"}>
                            <Button type="text" className="accounts-page__link-btn" onClick={(e) => toggleHiddenClientId(account.accountId, e)}>
                              {hiddenClientIds.includes(account.accountId) ? "Show" : "Hide"}
                            </Button>
                          </Tooltip>
                        </div>

                        <div className="accounts-page__status-cell">
                          <Tag
                            className={`accounts-page__status-tag ${
                              account.status === "Connected" ? "accounts-page__status-tag--ok" : "accounts-page__status-tag--bad"
                            }`}
                            style={{ fontSize: 11, padding: "2px 10px" }}
                          >
                            {account.status === "Connected" ? "Active" : "Inactive"}
                          </Tag>
                        </div>

                        <Space size={8} className="accounts-page__actions" onClick={(e) => e.stopPropagation()}>
                          <Popconfirm
                            title="Delete account"
                            description="Are you sure you want to delete this Databricks account?"
                            onConfirm={() => handleDeleteDatabricksAccount(account)}
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            cancelText="Cancel"
                          >
                            <Tooltip title="Delete">
                              <Button
                                icon={<DeleteFilled />}
                                type="text"
                                danger
                                size="small"
                                onClick={(e) => e.stopPropagation()}
                                disabled={isViewer}
                              />
                            </Tooltip>
                          </Popconfirm>
                          <Tooltip title="Configure Logs">
                            <Button
                              icon={<SettingOutlined />}
                              type="text"
                              size="small"
                              onClick={() => navigate(`/logs?accountId=databricks_${account.accountId}`)}
                            />
                          </Tooltip>
                          <Tooltip title="Configure Metrics">
                            <Button
                              icon={<BarChartOutlined />}
                              type="text"
                              size="small"
                              onClick={() => navigate(`/databricks-metrics-onboarding?accountId=databricks_${account.accountId}`)}
                            />
                          </Tooltip>
                        </Space>
                      </div>
                    </Card>
                  ))}
                </Spin>

                {filteredDatabricksAccounts.length > 0 && (
                  <div className="accounts-page__pagination-wrap">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={filteredDatabricksAccounts.length}
                      onChange={(page) => setCurrentPage(page)}
                      showSizeChanger
                      showTotal={(total) => <span>Total {total} accounts</span>}
                    />
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
{/* 
    <Table
    className='custom-ant-table rounded-rows-table'
     columns={columns}
     dataSource={accounts}
     rowKey="accountId"
     pagination={false}
    //  style={{ borderRadius: '8px', overflow: 'hidden', marginTop: 16 }}
    scroll={{ x: 'max-content'}} 
/> */}

      <Modal
        title="Select Onboarding Method"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleOnboard} requiredMark="optional">
          <Form.Item
            name="onboardMethod"
            label="Onboarding Method"
            rules={[{ required: true, message: 'Please select an onboarding method' }]}
          >
            <Radio.Group onChange={(e) => setOnboardMethod(e.target.value)} value={onboardMethod}>
              <Radio value="console">Use Amazon Console</Radio>
              <Radio value="accessKey">Use AWS Access/Secret Key</Radio>
              <Radio value="iamRole">Use IAM Role (recommended)</Radio>
            </Radio.Group>
          </Form.Item>

          {onboardMethod === 'accessKey' && (
            <>
              <Form.Item
                name="awsAccountName"
                label="AWS Account Name"
                rules={[{ required: true, message: 'Please enter AWS Account Name' }]}
              >
                <Input placeholder="Enter AWS Account Name" />
              </Form.Item>
              <Form.Item
                name="awsAccountId"
                label="AWS Account ID"
                rules={[{ validator: validateAwsAccountId }]}
              >
                <Input placeholder="123456789012" />
              </Form.Item>
              <Form.Item
                name="awsAccessKey"
                label="AWS Account Key"
                rules={[{ required: true, message: 'Please enter AWS Access Key' }]}
              >
                <Input placeholder="Enter AWS Access Key" />
              </Form.Item>
              <Form.Item
                name="awsSecretKey"
                label="AWS Secret Key"
                rules={[{ required: true, message: 'Please enter AWS Secret Key' }]}
              >
                <Input.Password placeholder="Enter AWS Secret Key" />
              </Form.Item>
              <Form.Item
                name="awsSessionToken"
                label="AWS Session Token"
                rules={[{ required: false }]}
              >
                <Input placeholder="Enter AWS Session Token (Optional)" />
              </Form.Item>
            </>
          )}

          {onboardMethod === 'iamRole' && (
            <>
              <Form.Item
                name="awsAccountName"
                label="AWS Account Name"
                rules={[{ required: true, message: 'Please enter AWS Account Name' }]}
              >
                <Input placeholder="Enter AWS Account Name" />
              </Form.Item>
              <Form.Item
                name="awsAccountId"
                label="AWS Account ID"
                rules={[{ validator: validateAwsAccountId }]}
              >
                <Input placeholder="123456789012" />
              </Form.Item>
              <Form.Item
                name="customerRoleArn"
                label="Customer Role ARN"
                rules={[{ validator: validateIamRoleArn }]}
              >
                <Input placeholder="arn:aws:iam::123456789012:role/YourCrossAccountRole" />
              </Form.Item>
              <Form.Item
                name="externalId"
                label="External ID (optional)"
                rules={[{ required: false }]}
              >
                <Input placeholder="External ID configured on your role (if any)" />
              </Form.Item>
            </>
          )}

          {/* For 'console', show a 'Coming Soon' alert with margin */}
          {onboardMethod === 'console' && (
            <div style={{ marginBottom: '16px' }}>
              <Alert message="Coming Soon" type="info"/>
            </div>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Proceed
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Databricks Account Modal */}
      <Modal
        title="Add Databricks Account"
        open={isAddDatabricksModalVisible}
        onCancel={() => {
          setIsAddDatabricksModalVisible(false);
          addDatabricksForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={addDatabricksForm}
          layout="vertical"
          onFinish={handleAddDatabricksAccount}
          requiredMark="optional"
        >
          <Form.Item
            name="accountId"
            label="Databricks Account ID"
            rules={[
              { required: true, message: 'Please enter Databricks Account ID' },
              { validator: validateUUID }
            ]}
          >
            <Input placeholder="Enter Databricks Account ID (UUID format)" />
          </Form.Item>

          <Form.Item
            name="accountUrl"
            label="Account URL"
            rules={[
              { required: true, message: 'Please enter Account URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
            initialValue="https://accounts.cloud.databricks.com"
          >
            <Input placeholder="https://accounts.cloud.databricks.com" />
          </Form.Item>

          <Form.Item
            name="clientId"
            label="OAuth Client ID"
            rules={[{ required: true, message: 'Please enter OAuth Client ID' }]}
          >
            <Input placeholder="Application ID (UUID format)" />
          </Form.Item>

          <Form.Item
            name="clientSecret"
            label="OAuth Client Secret"
            rules={[{ required: true, message: 'Please enter OAuth Client Secret' }]}
          >
            <Input.Password
              placeholder="Enter OAuth Client Secret"
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setIsAddDatabricksModalVisible(false);
                addDatabricksForm.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Onboard Account
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Pipeline Discovery Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DeploymentUnitOutlined style={{ color: '#1890ff' }} />
            <span>Discover Data Pipelines</span>
          </div>
        }
        open={isPipelineDiscoveryModalVisible}
        onCancel={() => {
          setIsPipelineDiscoveryModalVisible(false);
          setSelectedAccountForDiscovery(null);
          setDiscoveryResults(null);
          setOnboardingProgress(null);
          setAirflowEnvironments([]);
          setSelectedAirflowEnvironment(null);
        }}
        footer={null}
        width={900}
        destroyOnClose
        className="accounts-discovery-modal"
        rootClassName={isDark ? 'accounts-discovery-modal--dark' : 'accounts-discovery-modal--light'}
      >
        {selectedAccountForDiscovery && (
          <div>
            <Alert
              message={`Discovering pipelines for account: ${selectedAccountForDiscovery.aws_account_name || selectedAccountForDiscovery.accountId}`}
              type="info"
              style={{ marginBottom: 16 }}
              showIcon
            />

            {/* Discovery Options */}
            {!discoveryResults && (
              <div style={{ marginBottom: 16 }}>
                <Checkbox
                  checked={autoOnboardEnabled}
                  onChange={(e) => setAutoOnboardEnabled(e.target.checked)}
                  style={{ marginBottom: 12 }}
                >
                  Automatically onboard discovered logs
                </Checkbox>
                {autoOnboardEnabled && (
                  <div style={{ marginLeft: 24, marginTop: 8 }}>
                    <Checkbox
                      checked={onboardCloudwatch}
                      onChange={(e) => setOnboardCloudwatch(e.target.checked)}
                    >
                      Onboard CloudWatch logs
                    </Checkbox>
                    <br />
                    <Checkbox
                      checked={onboardS3}
                      onChange={(e) => setOnboardS3(e.target.checked)}
                      style={{ marginTop: 8 }}
                    >
                      Onboard S3 logs
                    </Checkbox>
                  </div>
                )}
              </div>
            )}

            {/* Discovery Button */}
            {!discoveryResults && (
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleStartDiscovery}
                loading={discoveryLoading}
                block
                size="large"
              >
                Start Discovery
              </Button>
            )}

            {/* Discovery Results */}
            {discoveryResults && (
              <div>
                {/* Summary Statistics */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col flex="1">
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                        {discoveryResults.summary.total_pipelines}
                      </div>
                      <div style={{ color: '#666', fontSize: 12 }}>Pipelines</div>
                    </Card>
                  </Col>
                  <Col flex="1">
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                        {discoveryResults.summary.total_glue_jobs}
                      </div>
                      <div style={{ color: '#666', fontSize: 12 }}>Glue Jobs</div>
                    </Card>
                  </Col>
                  <Col flex="1">
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 'bold', color: '#fa8c16' }}>
                        {discoveryResults.summary.total_step_functions}
                      </div>
                      <div style={{ color: '#666', fontSize: 12 }}>Step Functions</div>
                    </Card>
                  </Col>
                  <Col flex="1">
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 'bold', color: '#eb2f96' }}>
                        {discoveryResults.summary.total_emr_clusters}
                      </div>
                      <div style={{ color: '#666', fontSize: 12 }}>EMR Clusters</div>
                    </Card>
                  </Col>
                  <Col flex="1">
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 'bold', color: '#13c2c2' }}>
                        {discoveryResults.summary.total_airflow_dags
                          ?? discoveryResults.pipelines.airflow_dags?.length
                          ?? 0}
                      </div>
                      <div style={{ color: '#666', fontSize: 12 }}>Airflow DAGs</div>
                    </Card>
                  </Col>
                </Row>

                {/* Log Paths Summary */}
                <Card
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileTextOutlined />
                      <span>Discovered Log Paths</span>
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 'bold', color: '#1890ff' }}>
                          {discoveryResults.summary.total_cloudwatch_logs}
                        </div>
                        <div style={{ color: '#666' }}>CloudWatch Log Groups</div>
                        <div style={{ marginTop: 8 }}>
                          <Tag color="success">
                            {discoveryResults.summary.cloudwatch_onboarded || 0} onboarded
                          </Tag>
                          <Tag color="processing">
                            {discoveryResults.summary.cloudwatch_new || 0} new
                          </Tag>
                        </div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 'bold', color: '#52c41a' }}>
                          {discoveryResults.summary.total_s3_logs}
                        </div>
                        <div style={{ color: '#666' }}>S3 Log Paths</div>
                        <div style={{ marginTop: 8 }}>
                          <Tag color="success">
                            {discoveryResults.summary.s3_onboarded || 0} onboarded
                          </Tag>
                          <Tag color="processing">
                            {discoveryResults.summary.s3_new || 0} new
                          </Tag>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>

                {/* Pipeline Details */}
                <Tabs
                  className="accounts-discovery-modal-tabs"
                  defaultActiveKey="glue"
                  items={[
                    {
                      key: 'glue',
                      label: (
                        <span>
                          <ApiOutlined /> Glue Jobs ({discoveryResults.pipelines.glue_jobs?.length || 0})
                        </span>
                      ),
                      children: (
                        <div className="accounts-discovery-tab-panel">
                          {discoveryResults.pipelines.glue_jobs?.length > 0 ? (
                            discoveryResults.pipelines.glue_jobs.map((job, idx) => (
                              <Card
                                key={idx}
                                size="small"
                                className="accounts-discovery-pipeline-card"
                                style={{ marginBottom: 8 }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                  <div>
                                    <div className="accounts-discovery-pipeline-name">{job.name}</div>
                                    <Tag color="blue">{job.region}</Tag>
                                  </div>
                                </div>
                                {job.log_paths.cloudwatch.length > 0 && (
                                  <div className="accounts-discovery-pipeline-meta">
                                    <strong>CloudWatch:</strong> {job.log_paths.cloudwatch.length} log groups
                                  </div>
                                )}
                                {job.log_paths.s3.length > 0 && (
                                  <div className="accounts-discovery-pipeline-meta accounts-discovery-pipeline-meta--spaced">
                                    <strong>S3:</strong> {job.log_paths.s3.length} paths
                                  </div>
                                )}
                              </Card>
                            ))
                          ) : (
                            <Empty description="No Glue jobs found" />
                          )}
                        </div>
                      )
                    },
                    {
                      key: 'stepfunctions',
                      label: (
                        <span>
                          <DatabaseFilled /> Step Functions ({discoveryResults.pipelines.step_functions?.length || 0})
                        </span>
                      ),
                      children: (
                        <div className="accounts-discovery-tab-panel">
                          {discoveryResults.pipelines.step_functions?.length > 0 ? (
                            discoveryResults.pipelines.step_functions.map((sfn, idx) => (
                              <Card
                                key={idx}
                                size="small"
                                className="accounts-discovery-pipeline-card"
                                style={{ marginBottom: 8 }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                  <div>
                                    <div className="accounts-discovery-pipeline-name">{sfn.name}</div>
                                    <Tag color="blue">{sfn.region}</Tag>
                                    <PipelineRuntimeStatus pipeline={sfn} compact isDark={isDark} />
                                  </div>
                                </div>
                                {sfn.log_paths.cloudwatch.length > 0 && (
                                  <div className="accounts-discovery-pipeline-meta">
                                    <strong>CloudWatch:</strong> {sfn.log_paths.cloudwatch.length} log groups
                                  </div>
                                )}
                              </Card>
                            ))
                          ) : (
                            <Empty description="No Step Functions found" />
                          )}
                        </div>
                      )
                    },
                    {
                      key: 'emr',
                      label: (
                        <span>
                          <CloudOutlined /> EMR Clusters ({discoveryResults.pipelines.emr_clusters?.length || 0})
                        </span>
                      ),
                      children: (
                        <div className="accounts-discovery-tab-panel">
                          {discoveryResults.pipelines.emr_clusters?.length > 0 ? (
                            discoveryResults.pipelines.emr_clusters.map((emr, idx) => (
                              <Card
                                key={emr.id || idx}
                                size="small"
                                className="accounts-discovery-pipeline-card accounts-discovery-emr-card"
                                style={{ marginBottom: 8 }}
                              >
                                <div className="accounts-discovery-emr-card__header">
                                  <div className="accounts-discovery-emr-card__info">
                                    <div className="accounts-discovery-pipeline-name accounts-discovery-emr-card__name">
                                      {emr.name || 'Unnamed cluster'}
                                    </div>
                                    {emr.id ? (
                                      <div className="accounts-discovery-emr-card__id">{emr.id}</div>
                                    ) : null}
                                  </div>
                                  <div className="accounts-discovery-emr-card__status">
                                    <EmrClusterStatus pipeline={emr} compact isDark={isDark} />
                                  </div>
                                </div>
                                <Space size={4} wrap style={{ marginTop: 8 }}>
                                  <Tag color="blue">{emr.region}</Tag>
                                </Space>
                                <div className="accounts-discovery-emr-card__meta">
                                  <div><strong>Created:</strong> {formatEmrDateTime(emr.created_on)}</div>
                                  <div><strong>Elapsed:</strong> {formatEmrElapsedTime(emr)}</div>
                                </div>
                                {(emr.log_paths?.cloudwatch?.length ?? 0) > 0 && (
                                  <div className="accounts-discovery-emr-card__logs">
                                    <strong>CloudWatch:</strong> {emr.log_paths.cloudwatch.length} log groups
                                  </div>
                                )}
                                {(emr.log_paths?.s3?.length ?? 0) > 0 && (
                                  <div className="accounts-discovery-emr-card__logs">
                                    <strong>S3:</strong> {emr.log_paths.s3.length} paths
                                  </div>
                                )}
                              </Card>
                            ))
                          ) : (
                            <Empty description="No EMR clusters found" />
                          )}
                        </div>
                      )
                    },
                    {
                      key: 'airflow',
                      label: (
                        <span>
                          <DeploymentUnitOutlined /> Airflow DAGs ({discoveryResults.pipelines.airflow_dags?.length || 0})
                        </span>
                      ),
                      children: (
                        <div className="accounts-discovery-tab-panel">
                          <div
                            style={{
                              marginBottom: 12,
                              display: 'flex',
                              gap: 8,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Select
                              placeholder={
                                airflowEnvironmentsLoading
                                  ? 'Loading MWAA environments...'
                                  : 'Select Airflow (MWAA) environment'
                              }
                              value={selectedEnvNameForAirflow}
                              onChange={(name) => {
                                const env =
                                  airflowEnvironments.find((e) => e.name === name)
                                  || { name };
                                setSelectedAirflowEnvironment(env);
                              }}
                              style={{ minWidth: 280, flex: 1 }}
                              loading={airflowEnvironmentsLoading}
                              allowClear
                              options={airflowEnvironments.map((env) => ({
                                value: env.name,
                                label: env.region
                                  ? `${env.name} (${env.region})`
                                  : env.name,
                              }))}
                            />
                            <Button
                              type="primary"
                              icon={<ThunderboltOutlined />}
                              onClick={handleDiscoverAirflowDags}
                              loading={airflowPipelinesLoading}
                              disabled={!selectedEnvNameForAirflow}
                            >
                              Discover DAGs
                            </Button>
                          </div>
                          {discoveryResults.pipelines.airflow_dags?.length > 0 ? (
                            discoveryResults.pipelines.airflow_dags.map((dag, idx) => (
                              <Card
                                key={idx}
                                size="small"
                                className="accounts-discovery-pipeline-card"
                                style={{ marginBottom: 8 }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12 }}>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div className="accounts-discovery-pipeline-name">{dag.name}</div>
                                    <div className="accounts-discovery-pipeline-subtitle">
                                      {getAirflowEnvironmentName(dag) || '—'}
                                    </div>
                                    <Space size={4} wrap>
                                      <Tag color="blue">{dag.region}</Tag>
                                    </Space>
                                  </div>
                                  <AirflowDagStatus pipeline={dag} compact isDark={isDark} />
                                </div>
                                {dag.log_paths?.cloudwatch?.length > 0 && (
                                  <div className="accounts-discovery-pipeline-meta">
                                    <strong>CloudWatch:</strong> {dag.log_paths.cloudwatch.length} log groups
                                  </div>
                                )}
                              </Card>
                            ))
                          ) : (
                            <Empty
                              description={
                                airflowEnvironments.length > 0
                                  ? 'Select an MWAA environment and click Discover DAGs'
                                  : 'No MWAA environments found in this account'
                              }
                            />
                          )}
                        </div>
                      )
                    }
                  ]}
                />

                {/* Onboarding Progress */}
                {onboardingProgress && (
                  <Card
                    title="Onboarding Results"
                    style={{
                      marginTop: 16,
                      background: isDark ? '#161b22' : '#fff',
                      borderColor: isDark ? '#30363d' : '#e8e8e8',
                    }}
                    styles={{ header: { color: isDark ? '#e6edf3' : '#0f172a' } }}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <strong style={{ color: isDark ? '#e6edf3' : '#0f172a' }}>CloudWatch Logs:</strong>
                      <div style={{ marginLeft: 16, marginTop: 4 }}>
                        <Tag color="success">
                          {onboardingProgress.cloudwatch?.onboarded?.length || 0} newly onboarded
                        </Tag>
                        {(onboardingProgress.cloudwatch?.already_onboarded?.length || 0) > 0 && (
                          <Tag color="default">
                            {onboardingProgress.cloudwatch.already_onboarded.length} already onboarded
                          </Tag>
                        )}
                        {onboardingProgress.cloudwatch?.failed?.length > 0 && (
                          <Tag color="error">
                            {onboardingProgress.cloudwatch.failed.length} failed
                          </Tag>
                        )}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: isDark ? '#e6edf3' : '#0f172a' }}>S3 Logs:</strong>
                      <div style={{ marginLeft: 16, marginTop: 4 }}>
                        <Tag color="success">
                          {onboardingProgress.s3?.onboarded?.length || 0} newly onboarded
                        </Tag>
                        {(onboardingProgress.s3?.already_onboarded?.length || 0) > 0 && (
                          <Tag color="default">
                            {onboardingProgress.s3.already_onboarded.length} already onboarded
                          </Tag>
                        )}
                        {onboardingProgress.s3?.failed?.length > 0 && (
                          <Tag color="error">
                            {onboardingProgress.s3.failed.length} failed
                          </Tag>
                        )}
                      </div>
                    </div>
                    {(onboardingProgress.cloudwatch?.onboarded?.length || 0) === 0 &&
                      (onboardingProgress.s3?.onboarded?.length || 0) === 0 &&
                      ((onboardingProgress.cloudwatch?.already_onboarded?.length || 0) > 0 ||
                        (onboardingProgress.s3?.already_onboarded?.length || 0) > 0) && (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginTop: 12 }}
                        message="All selected log paths are already onboarded"
                      />
                    )}
                  </Card>
                )}

                {/* Action Buttons */}
                {!onboardingProgress && (
                  <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                    <Button
                      type="primary"
                      icon={<FileTextOutlined />}
                      onClick={handleOnboardDiscoveredLogs}
                      loading={discoveryLoading}
                      disabled={discoveryResults.summary.total_log_paths === 0}
                    >
                      Onboard All Logs
                    </Button>
                    <Button
                      onClick={() => {
                        setIsPipelineDiscoveryModalVisible(false);
                        setDiscoveryResults(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                )}

                {onboardingProgress && (
                  <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setIsPipelineDiscoveryModalVisible(false);
                        setDiscoveryResults(null);
                        setOnboardingProgress(null);
                        // Refresh logs page if user wants
                        message.success('Logs onboarded! You can view them in the Logs page.');
                      }}
                    >
                      Done
                    </Button>
                  </div>
                )}
              </div>
            )}

            {discoveryLoading && !discoveryResults && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#666' }}>Discovering pipelines...</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AccountsPage;
